import React, { useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { Stack, Chip, FormControlLabel, Tooltip, useMediaQuery, Theme, Box, IconButton, alpha } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { DatePicker, DateRangePicker } from '@e3dc-react/shell/Elements/DatePicker'
import { ChartView } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { ErrorDateFormats } from '@e3dc-react/shell/Types/globalTypes'
import { getDateRangeViews, getDateViews, getOpenToRangeValue, getOpenToValue } from '../../utils/date-handling'
import { useDateHandling } from '../../hooks'
import { E3Forecast } from '@e3dc-react/icons'
import { CheckboxField } from '@e3dc-react/shell/Elements/InputFields'

const FORECAST_DAYS = 1
const MIN_FROM_TO_DISTANCE = 1
const CUSTOM_BREAKPOINT_TABLET = 785

const useStyles = makeStyles<{ isForecastEnabled: boolean }>()((theme, { isForecastEnabled }) => ({
  root: {
    display: 'flex',
    flex: 1,
    paddingVertical: theme.spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up(CUSTOM_BREAKPOINT_TABLET)]: {
      flexDirection: 'row',
    },
    gap: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  datePickerWrapper: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  pointer: {
    flex: 1,
  },
  label: {
    marginLeft: 0,
    marginRight: 0,
  },
  forecastIconButton: {
    backgroundColor: isForecastEnabled ? theme.palette.primary.main : alpha(theme.palette.grey.grey00, 0.08),
    borderRadius: theme.spacing(2),
    '&:hover': {
      backgroundColor: isForecastEnabled ? theme.palette.primary.main : alpha(theme.palette.grey.grey00, 0.12),
      cursor: 'pointer',
    },
  },
}))

const chartViews: ChartView[] = ['day', 'week', 'month', 'year']

interface HeaderProps {
  minFrom?: string
  maxTS?: Moment
  maxFrom?: Moment
  maxTo?: Moment
  view: ChartView
  from: Moment
  to: Moment
  customRange: boolean
  hasForecastOption: boolean
  isForecastEnabled: boolean
  onViewChange: (view: ChartView) => void
  onFromChange: (from: Moment | null, view?: ChartView) => void
  onToChange: (from: Moment | null) => void
  onCustomRangeChange: (bool: boolean) => void
  onMinus: () => void
  onPlus: () => void
  onShowForecast: (forecast: boolean) => void
}

/**
 * Allows to configure the date settings of a chart.
 * - custom range: allows to select a custom date range
 * - show forecast: shows the forecast for the selected date range
 * - view: allows to select the view of the chart (day, week, month, year)
 */
const ChartDateSelect: React.FC<HeaderProps> = ({
  minFrom,
  maxTS,
  maxFrom,
  maxTo,
  view,
  from,
  to,
  customRange,
  hasForecastOption,
  isForecastEnabled,
  onViewChange,
  onFromChange,
  onToChange,
  onCustomRangeChange,
  onMinus,
  onPlus,
  onShowForecast,
}) => {
  const { t } = useCustomTranslation()
  const theme = useTheme<Theme>()
  const upCustomTablet = useMediaQuery(theme.breakpoints.up(CUSTOM_BREAKPOINT_TABLET))
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const { classes } = useStyles({ isForecastEnabled })
  const { getDateFormat, getDateRangeFormat } = useDateHandling()
  const isDayViewAndNotToday = view === 'day' && !moment().isSame(from, 'day')

  const maxDateForCustomRange = useMemo(() => {
    if (view === 'year') return moment()
    /* handle custom range maxToDate */
    if (view === 'day' || view === 'week') {
      return moment().add(FORECAST_DAYS, 'days').endOf('day')
    }
    return maxTo ?? maxTS
  }, [maxTo, view, maxTS])

  const minDateForCustomRange = useMemo(() => {
    if (view === 'day') return from.clone()
    if (view === 'week' || view === 'month') return from.clone().add(MIN_FROM_TO_DISTANCE, 'day')
    if (view === 'year') return from.clone().add(MIN_FROM_TO_DISTANCE, 'month')
  }, [from, view])

  const fromMaxDate = useMemo(() => maxFrom ?? moment().endOf('day'), [maxFrom])

  const DatePickerVariants =
    upMd && customRange ? (
      <DateRangePicker
        inputFormat={getDateRangeFormat(view)}
        ampm={false}
        views={getDateRangeViews(view)}
        openTo={getOpenToRangeValue(view)}
        fromLabel={t('systemDataHandler.chartHeader.customRangeFrom')}
        toLabel={t('systemDataHandler.chartHeader.customRangeTo')}
        valueFrom={from}
        valueTo={to}
        handleToChange={onToChange}
        handleFromChange={onFromChange}
        onNextClick={onPlus}
        onPrevClick={onMinus}
        toMinDate={minDateForCustomRange}
        toMaxDate={maxDateForCustomRange}
        fromMaxDate={fromMaxDate}
        fromMinDate={moment(minFrom)}
        toMaxDateMessage={t('systemDataHandler.chartHeader.fromMaxDateMessage', { max: maxTS?.format(t('common.date')) })}
        minutesStep={15}
        dataTagPrefix="chartDateSelect"
        withoutNextPreviousButtons={true}
        errorDateFormat={view === 'year' ? ErrorDateFormats.MonthDateFormat : t('common.format.date')}
      />
    ) : (
      <DatePicker
        value={from}
        handleDateChange={onFromChange}
        inputFormat={getDateFormat(view)}
        views={getDateViews(view)}
        openTo={getOpenToValue(view)}
        minDate={moment(minFrom).startOf(view)}
        maxFrom={maxFrom}
        maxDate={maxFrom}
        onNextClick={onPlus}
        onPrevClick={onMinus}
        pointerClass={classes.pointer}
        data-qa={'system-charts-date-picker'}
      />
    )

  const SelectCustomRange = (
    <Tooltip title={t('systemDataHandler.chartHeader.customRangeTooltip') as string}>
      <Box component={'span'} pl={1}>
        <FormControlLabel
          control={<CheckboxField value={customRange} onChange={value => onCustomRangeChange(value)} data-qa={'checkbox-custom-range'} />}
          label={t('systemDataHandler.chartHeader.customRangeCheckbox')}
        />
      </Box>
    </Tooltip>
  )

  const SelectShowForecast = view === 'day' && (
    <Tooltip title={t('systemDataHandler.chartHeader.showForecastTooltip') as string}>
      <span>
        <FormControlLabel
          className={classes.label}
          sx={{ gap: 1 }}
          control={
            <IconButton
              className={classes.forecastIconButton}
              size="small"
              onClick={() => onShowForecast(!isForecastEnabled)}
              data-qa={'info-icon-show-forecast'}
            >
              <E3Forecast sx={{ color: isForecastEnabled ? theme.palette.common.white : theme.palette.grey.grey00 }} />
            </IconButton>
          }
          label={upMd ? t('systemDataHandler.chartHeader.showForecastCheckbox') : ''}
        />
      </span>
    </Tooltip>
  )

  const SelectView = (
    <Stack direction="row" rowGap={1} columnGap={1} flexWrap="wrap" flex={1}>
      {chartViews.map(viewOption => (
        <Chip
          sx={{
            flex: 1,
            ...(view === viewOption && {
              '&:hover, &:focus, &:active': {
                backgroundColor: theme => theme.palette.primary.main,
              },
            }),
          }}
          key={viewOption}
          label={isDayViewAndNotToday && viewOption === 'day' ? t('systemDataHandler.views.today') : t(`systemDataHandler.views.${viewOption}`)}
          color={view === viewOption ? 'primary' : 'default'}
          onClick={() => onViewChange(viewOption)}
          data-qa={`chart-view-${viewOption}`}
        />
      ))}
    </Stack>
  )

  if (upCustomTablet) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 2 }} id="dashboard-datebar">
        <Box sx={{ flex: 1 }}>
          {upMd && SelectCustomRange}
          {hasForecastOption && SelectShowForecast}
        </Box>
        <Box className={classes.datePickerWrapper} sx={{ flex: 1, alignItem: 'center', justifyContent: 'center' }}>
          {DatePickerVariants}
        </Box>
        {SelectView}
      </Box>
    )
  }
  return (
    <Box className={classes.root} id="dashboard-datebar">
      <Box sx={{ width: '100%' }}>{SelectView}</Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        {hasForecastOption && <Box sx={{ display: 'flex', flex: 0 }}>{SelectShowForecast}</Box>}
        <Box className={classes.datePickerWrapper}>{DatePickerVariants}</Box>
      </Box>
    </Box>
  )
}

export default ChartDateSelect
